// src/components/risk/InterventionPanel.jsx
// Intervention modal with SMS/Call/Email and outcome logging

import React, { useState, useEffect } from 'react';
import {
  X,
  Phone,
  MessageSquare,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  UserX,
  ArrowUp,
  Loader2,
  RefreshCw,
  Calendar,
  ClipboardCheck
} from 'lucide-react';
import { INTERVENTION_OUTCOMES, getRiskCategory } from './constants';

const BASE = process.env.REACT_APP_API_URL || "";

const InterventionPanel = ({
  isOpen,
  onClose,
  client,
  initialType = 'call',
  onInterventionComplete
}) => {
  const [step, setStep] = useState('compose'); // compose, outcome, success
  const [interventionType, setInterventionType] = useState(initialType);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(initialType === 'annual_review' ? 'annual_review' : 'compose');
      setInterventionType(initialType);
      setMessage('');
      setSelectedOutcome(null);
      setNotes('');
      setResult(null);
      setError(null);
      setScheduledDate('');
    }
  }, [isOpen, initialType]);

  // Pre-fill message templates based on intervention type
  const getDefaultMessage = (type) => {
    const firstName = client?.first_name || 'there';
    switch (type) {
      case 'sms':
        return `Hi ${firstName}, this is your insurance agent checking in. How are you doing? Do you have any questions about your coverage? Reply to let me know!`;
      case 'email':
        return `Dear ${firstName},\n\nI hope this message finds you well. I wanted to reach out personally to see how things are going with your current coverage.\n\nIf you have any questions or concerns, please don't hesitate to reach out.\n\nBest regards`;
      default:
        return '';
    }
  };

  const handleTypeChange = (type) => {
    setInterventionType(type);
    if (type === 'sms' || type === 'email') {
      setMessage(getDefaultMessage(type));
    }
  };

  // Send intervention
  const handleSendIntervention = async () => {
    setSending(true);
    setError(null);

    try {
      if (interventionType === 'sms' && message.trim()) {
        // Send SMS
        const smsRes = await fetch(`${BASE}/comms/sms/send`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientId: client.id,
            message: message.trim()
          })
        });

        if (!smsRes.ok) {
          const errData = await smsRes.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to send SMS');
        }
      } else if (interventionType === 'email' && message.trim()) {
        // Email send endpoint not yet implemented — log as communication record
        const emailRes = await fetch(`${BASE}/comms`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientId: client.id,
            type: 'email',
            direction: 'outbound',
            subject: `Check-in from your insurance agent`,
            body: message.trim()
          })
        });

        if (!emailRes.ok) {
          const errData = await emailRes.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to log email');
        }
      }
      // For calls, we just log the attempt - proceed to outcome

      // Move to outcome step
      setStep('outcome');
    } catch (err) {
      console.error('Intervention error:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Log intervention outcome
  const handleLogOutcome = async () => {
    if (!selectedOutcome) return;

    setSending(true);
    setError(null);

    try {
      const res = await fetch(`${BASE}/churn-prediction/call-outcome`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: client.id,
          outcomeCode: selectedOutcome,
          notes: notes.trim(),
          contactMethod: interventionType
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to log outcome');
      }

      setResult(data);
      setStep('success');

      // Dispatch event to refresh risk score displays across the app
      window.dispatchEvent(new CustomEvent('risk-score-updated', { detail: { clientId: client.id } }));

      // Notify parent
      onInterventionComplete?.({
        clientId: client.id,
        outcome: selectedOutcome,
        newScore: data.newScore,
        scoreChange: data.scoreChange,
        savedClient: data.savedClient
      });
    } catch (err) {
      console.error('Outcome logging error:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Handle annual review scheduling
  const handleScheduleAnnualReview = async () => {
    if (!scheduledDate) return;

    setSending(true);
    setError(null);

    try {
      // Create a task for the annual review
      const res = await fetch(`${BASE}/tasks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: client.id,
          title: 'Annual Plan Review',
          description: `Scheduled annual plan review for ${client?.first_name || 'client'}`,
          dueDate: scheduledDate,
          priority: 'high',
          taskType: 'annual_review'
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to schedule annual review');
      }

      setResult({ scheduled: true, dueDate: scheduledDate });
      setStep('success');

      onInterventionComplete?.({
        clientId: client.id,
        action: 'annual_review_scheduled',
        scheduledDate
      });
    } catch (err) {
      console.error('Schedule error:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Handle annual review completion
  const handleCompleteAnnualReview = async () => {
    setSending(true);
    setError(null);

    try {
      // Use the dedicated complete-annual-review endpoint
      // This properly sets is_annual_review=true in client_contacts,
      // logs a satisfied call outcome, and triggers Lambda scoring
      const res = await fetch(`${BASE}/churn-prediction/complete-annual-review`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: client.id,
          notes: notes.trim() || 'Annual review completed'
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to complete annual review');
      }

      setResult({
        completed: true,
        newScore: data.newScore,
        previousScore: data.previousScore,
        scoreChange: data.scoreChange,
        savedClient: data.savedClient
      });
      setStep('success');

      // Dispatch event to refresh risk score displays
      window.dispatchEvent(new CustomEvent('risk-score-updated', { detail: { clientId: client.id } }));

      onInterventionComplete?.({
        clientId: client.id,
        action: 'annual_review_completed',
        newScore: data.newScore,
        scoreChange: data.scoreChange,
        savedClient: data.savedClient
      });
    } catch (err) {
      console.error('Complete annual review error:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const category = getRiskCategory(client?.risk_score || 0);
  const clientName = `${client?.first_name || ''} ${client?.last_name || ''}`.trim() || 'Client';

  // Icon map for outcomes - keys match call_outcome_options.outcome_code
  const outcomeIcons = {
    contact_satisfied: CheckCircle2,
    contact_followup: Clock,
    no_answer_voicemail: Phone,
    bad_phone_number: UserX,
    escalate_manager: ArrowUp
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800">
          <div className="flex items-center gap-3">
            {interventionType === 'annual_review' && <Calendar className="w-5 h-5 text-amber-400" />}
            {interventionType === 'call' && <Phone className="w-5 h-5 text-green-400" />}
            {interventionType === 'sms' && <MessageSquare className="w-5 h-5 text-blue-400" />}
            {interventionType === 'email' && <Mail className="w-5 h-5 text-purple-400" />}
            <div>
              <h2 className="text-lg font-semibold text-white">
                {step === 'annual_review' && 'Annual Plan Review'}
                {step === 'compose' && `Contact ${clientName}`}
                {step === 'outcome' && 'Log Outcome'}
                {step === 'success' && 'Complete'}
              </h2>
              <p className="text-sm text-slate-400">
                Risk Score: <span style={{ color: category.color }}>{Math.round(client?.risk_score || 0)}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Annual Review Step */}
          {step === 'annual_review' && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Annual Plan Review</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Schedule or complete the annual review for {clientName}
                </p>
              </div>

              {/* Schedule option */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-600">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  Schedule Review
                </h4>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500"
                />
                <button
                  onClick={handleScheduleAnnualReview}
                  disabled={!scheduledDate || sending}
                  className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      Schedule Review
                    </>
                  )}
                </button>
              </div>

              {/* Complete option */}
              <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-600">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-green-400" />
                  Mark as Complete
                </h4>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
                  placeholder="Notes about the review (optional)..."
                />
                <button
                  onClick={handleCompleteAnnualReview}
                  disabled={sending}
                  className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Complete Annual Review
                    </>
                  )}
                </button>
              </div>

              {/* Or call the client */}
              <div className="text-center">
                <button
                  onClick={() => setStep('compose')}
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Or contact client via call/SMS/email
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Compose */}
          {step === 'compose' && (
            <div className="space-y-4">
              {/* Type selector */}
              <div className="flex gap-2">
                {client?.phone && (
                  <>
                    <button
                      onClick={() => handleTypeChange('call')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        interventionType === 'call'
                          ? 'bg-green-600 border-green-500 text-white'
                          : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </button>
                    <button
                      onClick={() => handleTypeChange('sms')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        interventionType === 'sms'
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      SMS
                    </button>
                  </>
                )}
                {client?.email && (
                  <button
                    onClick={() => handleTypeChange('email')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      interventionType === 'email'
                        ? 'bg-purple-600 border-purple-500 text-white'
                        : 'bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </button>
                )}
              </div>

              {/* Contact info display */}
              <div className="p-3 bg-slate-800/50 rounded-lg">
                {interventionType === 'call' && client?.phone && (
                  <div className="flex items-center gap-2 text-white">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span>{client.phone}</span>
                    <a
                      href={`tel:${client.phone}`}
                      className="ml-auto text-sm text-amber-400 hover:text-amber-300"
                    >
                      Dial Now
                    </a>
                  </div>
                )}
                {interventionType === 'sms' && client?.phone && (
                  <div className="flex items-center gap-2 text-white">
                    <MessageSquare className="w-4 h-4 text-slate-500" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {interventionType === 'email' && client?.email && (
                  <div className="flex items-center gap-2 text-white">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span>{client.email}</span>
                  </div>
                )}
              </div>

              {/* Message composer (for SMS/Email) */}
              {(interventionType === 'sms' || interventionType === 'email') && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={interventionType === 'email' ? 6 : 3}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
                    placeholder={`Enter your ${interventionType === 'sms' ? 'SMS' : 'email'} message...`}
                  />
                  {interventionType === 'sms' && (
                    <div className="mt-1 text-xs text-slate-500 text-right">
                      {message.length} / 160 characters
                    </div>
                  )}
                </div>
              )}

              {/* Action button */}
              <button
                onClick={handleSendIntervention}
                disabled={sending || ((interventionType === 'sms' || interventionType === 'email') && !message.trim())}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-medium rounded-lg transition-colors"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : interventionType === 'call' ? (
                  <>
                    <Phone className="w-5 h-5" />
                    Log Call Attempt
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send {interventionType === 'sms' ? 'SMS' : 'Email'}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Log Outcome */}
          {step === 'outcome' && (
            <div className="space-y-4">
              <p className="text-slate-300">How did the conversation go?</p>

              {/* Outcome options */}
              <div className="space-y-2">
                {INTERVENTION_OUTCOMES.map((outcome) => {
                  const IconComponent = outcomeIcons[outcome.value] || CheckCircle2;
                  const isSelected = selectedOutcome === outcome.value;

                  return (
                    <button
                      key={outcome.value}
                      onClick={() => setSelectedOutcome(outcome.value)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-amber-500 bg-amber-500/10'
                          : 'border-slate-600 bg-slate-800 hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          isSelected ? 'bg-amber-500/20' : 'bg-slate-700'
                        }`}>
                          <IconComponent className={`w-5 h-5 ${
                            isSelected ? 'text-amber-400' : 'text-slate-400'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-white">{outcome.label}</div>
                          <div className="text-sm text-slate-400">{outcome.description}</div>
                        </div>
                        {outcome.scoreImpact !== 0 && (
                          <span className={`text-sm font-medium ${
                            outcome.scoreImpact === 'reset' ? 'text-green-400' :
                            outcome.scoreImpact < 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {outcome.scoreImpact === 'reset' ? 'Resets' :
                             outcome.scoreImpact > 0 ? `+${outcome.scoreImpact}` : outcome.scoreImpact}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
                  placeholder="Add any notes about the conversation..."
                />
              </div>

              {/* Submit button */}
              <button
                onClick={handleLogOutcome}
                disabled={!selectedOutcome || sending}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-medium rounded-lg transition-colors"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Log Outcome
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 'success' && result && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                {result.scheduled ? (
                  <Calendar className="w-8 h-8 text-blue-400" />
                ) : (
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                )}
              </div>

              <h3 className="text-xl font-semibold text-white mb-2">
                {result.scheduled ? 'Review Scheduled' :
                 result.completed ? 'Annual Review Complete' :
                 'Intervention Logged'}
              </h3>

              {result.scheduled && (
                <div className="mb-4">
                  <p className="text-slate-400">Scheduled for:</p>
                  <p className="text-lg text-white font-medium mt-1">
                    {new Date(result.dueDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}

              {result.scoreChange && (
                <div className="mb-4">
                  <p className="text-slate-400">Score updated:</p>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-slate-500">{Math.round(result.previousScore || (result.newScore - result.scoreChange))}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-2xl font-bold" style={{ color: getRiskCategory(result.newScore).color }}>
                      {Math.round(result.newScore)}
                    </span>
                    <span className={`text-sm font-medium ${result.scoreChange < 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ({result.scoreChange > 0 ? '+' : ''}{Math.round(result.scoreChange)})
                    </span>
                  </div>
                </div>
              )}

              {result.savedClient && (
                <div className="p-3 bg-green-900/30 border border-green-500/30 rounded-lg mb-4">
                  <div className="flex items-center justify-center gap-2 text-green-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Client Saved!</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    This client has been moved out of the high-risk category
                  </p>
                </div>
              )}

              <button
                onClick={onClose}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterventionPanel;
